import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, Sparkles, Shield, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from '../config/branding';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    cabinet: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      const { confirmPassword, ...data } = formData;
      await authAPI.register(data);
      toast.success('Compte créé avec succès');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex">
      {/* Left side - Screenshot Preview */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        {/* Background - Screenshot of a modern dashboard/chat app */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')`,
          }}
        />
        {/* Dark overlay with gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/95 via-[#0F172A]/80 to-[#0F172A]/90" />
        
        {/* Header */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt={BRAND.name} className="w-10 h-10" />
            <span className="text-2xl font-heading font-semibold tracking-tight text-[#F8FAFC]">
              {BRAND.name}
            </span>
          </div>
          <Link 
            to="/pricing" 
            className="text-sm text-[#B69D74] hover:text-[#D4B896] transition-colors font-medium"
          >
            Nos tarifs
          </Link>
        </div>
        
        {/* Main content */}
        <div className="relative z-10 max-w-lg">
          <h1 className="text-4xl lg:text-5xl font-heading font-semibold tracking-tight leading-tight mb-6 text-[#F8FAFC]">
            Rejoignez
            <br />
            <span className="text-[#B69D74]">l'excellence juridique</span>
          </h1>
          <p className="text-lg text-[#94A3B8] leading-relaxed mb-8">
            Créez votre compte et accédez à une suite complète d'outils 
            pour optimiser votre pratique du droit.
          </p>
          
          {/* Feature highlights */}
          <div className="space-y-4">
            {[
              { icon: Sparkles, text: 'IA juridique de dernière génération' },
              { icon: Shield, text: 'Données sécurisées et confidentielles' },
              { icon: Zap, text: 'Recherches 10x plus rapides' },
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-4 animate-slide-up" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="w-px h-4 bg-[#B69D74]" />
                <feature.icon className="w-4 h-4 text-[#B69D74]" strokeWidth={1.5} />
                <span className="text-sm text-[#94A3B8]">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stats */}
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-[#B69D74]/50" />
            <span className="text-[10px] text-[#94A3B8]/60 uppercase tracking-widest">
              Déjà adopté par
            </span>
          </div>
          <div className="flex gap-8">
            <div>
              <p className="text-2xl font-heading font-bold text-[#F8FAFC]">500+</p>
              <p className="text-xs text-[#94A3B8]/60">Avocats</p>
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-[#F8FAFC]">50k+</p>
              <p className="text-xs text-[#94A3B8]/60">Recherches/mois</p>
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-[#F8FAFC]">98%</p>
              <p className="text-xs text-[#94A3B8]/60">Satisfaction</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#0F172A]">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 lg:hidden mb-10">
            <img src="/logo.png" alt={BRAND.name} className="w-8 h-8" />
            <span className="text-xl font-heading font-bold text-white">{BRAND.name}</span>
          </div>
          
          <div className="mb-8">
            <p className="text-xs uppercase tracking-widest text-[#B69D74] mb-2">INSCRIPTION</p>
            <h2 className="text-2xl font-heading font-semibold text-[#F8FAFC] mb-1">
              Créer un compte
            </h2>
            <p className="text-sm text-[#94A3B8]">
              Remplissez le formulaire ci-dessous
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prenom" className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                  Prénom
                </Label>
                <Input
                  id="prenom"
                  name="prenom"
                  placeholder="Jean"
                  value={formData.prenom}
                  onChange={handleChange}
                  required
                  className="h-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#B69D74] focus:ring-0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nom" className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                  Nom
                </Label>
                <Input
                  id="nom"
                  name="nom"
                  placeholder="Dupont"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  className="h-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#B69D74] focus:ring-0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cabinet" className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                Cabinet (optionnel)
              </Label>
              <Input
                id="cabinet"
                name="cabinet"
                placeholder="Cabinet Dupont & Associés"
                value={formData.cabinet}
                onChange={handleChange}
                className="h-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#B69D74] focus:ring-0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="avocat@cabinet.fr"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#B69D74] focus:ring-0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                Mot de passe
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Min. 8 caractères"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                className="h-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#B69D74] focus:ring-0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                Confirmer le mot de passe
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirmer"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="h-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#B69D74] focus:ring-0"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-11 mt-2 bg-gradient-to-r from-[#B69D74] to-[#8B7355] text-white font-semibold uppercase tracking-wider hover:opacity-90 gap-2"
              disabled={loading}
            >
              {loading ? (
                <span className="spinner" />
              ) : (
                <>
                  Créer mon compte
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </>
              )}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-[#94A3B8]">
            Déjà un compte ?{' '}
            <Link 
              to="/login" 
              className="text-[#B69D74] hover:text-[#D4B896] font-medium transition-colors"
            >
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
